import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Flex,
  Text,
  Box,
  Grid,
  GridItem,
  Input,
  Select,
  SimpleGrid,
  HStack,
  Spinner,
} from '@chakra-ui/react';
import { MapPin } from 'phosphor-react';
import moment from 'moment';
import { useDebounceCallback } from 'usehooks-ts';
import { PageBox } from '../../../../../../components/pageBox';
import { ReturnButton } from '../../../../../../components/buttons/ReturnButton';
import { MonthNavigator } from '../../../../../../components/shared/MonthNavigator';
import { useMonthFilter } from '../../../../../../hooks/useMonthFilter';
import { useIcms } from '../../../../../../hooks/useIcms';
import { DifalResumoEmpresaCards } from '../components/DifalKpis';
import { DifalNotasTable } from '../components/DifalNotasTable';
import { DifalPorUfChart } from '../components/DifalPorUfChart';

import { UF_LIST } from '../../../../../../utils/constants';

export function IcmsDifalEmpresaPage() {
  const { codiEmp } = useParams<{ codiEmp: string }>();
  const codiEmpNum = Number(codiEmp);
  const { getDifalResumo, getDifalPorUf, getDifalNotas } = useIcms();

  const { currentDate, dateStart, dateEnd, handlePrevMonth, handleNextMonth, handleSelectMonth } =
    useMonthFilter({ defaultToPreviousMonth: true });

  const [searchParams, setSearchParams] = useSearchParams();
  const tipo = searchParams.get('tipo') || '';
  const ufDestino = searchParams.get('uf_destino') || '';
  const codiAcu = searchParams.get('codi_acu') || '';
  const [codiAcuInput, setCodiAcuInput] = useState(codiAcu);
  const page = parseInt(searchParams.get('page') || '1', 10);
  const size = parseInt(searchParams.get('size') || '50', 10);

  const { data: resumoData, isLoading: isLoadingResumo } = useQuery({
    queryKey: ['difal-resumo', { codi_emp: codiEmpNum, dt_inicio: dateStart, dt_fim: dateEnd }],
    queryFn: getDifalResumo,
    enabled: !!codiEmpNum && !!dateStart && !!dateEnd,
    staleTime: 5 * 60 * 1000,
  });

  const { data: porUfData, isLoading: isLoadingUf } = useQuery({
    queryKey: ['difal-por-uf', { codi_emp: codiEmpNum, dt_inicio: dateStart, dt_fim: dateEnd }],
    queryFn: getDifalPorUf,
    enabled: !!codiEmpNum && !!dateStart && !!dateEnd,
    staleTime: 5 * 60 * 1000,
  });

  const notasFilters = useMemo(
    () => ({
      codi_emp: codiEmpNum,
      dt_inicio: dateStart,
      dt_fim: dateEnd,
      ...(tipo ? { tipo } : {}),
      ...(ufDestino ? { uf_destino: ufDestino } : {}),
      ...(codiAcu ? { codi_acu: codiAcu } : {}),
      page,
      size,
    }),
    [codiEmpNum, dateStart, dateEnd, tipo, ufDestino, codiAcu, page, size],
  );

  const tableQuery = useQuery({
    queryKey: ['difal-notas', notasFilters],
    queryFn: getDifalNotas,
    enabled: !!codiEmpNum && !!dateStart && !!dateEnd,
    placeholderData: prev => prev,
  });

  const items = tableQuery.data?.data?.items ?? [];
  const pagination = {
    total: tableQuery.data?.data?.total ?? 0,
    pages: tableQuery.data?.data?.pages ?? 0,
    page: tableQuery.data?.data?.page ?? 1,
    size: tableQuery.data?.data?.size ?? size,
    next_page: tableQuery.data?.data?.next_page ?? null,
    previous_page: tableQuery.data?.data?.previous_page ?? null,
  };

  const setQueryParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const debouncedCodiAcu = useDebounceCallback((value: string) => {
    setQueryParam('codi_acu', value);
  }, 400);

  return (
    <PageBox>
      <Flex align="center" pb={5} wrap="wrap" gap={2}>
        <ReturnButton previousPage="/modulesHome/supervisor/icms/difal" />
        <Box h="full" flexShrink={0}>
          <Flex align="center" justify="center" borderRadius="full" p={3} bg="#FFF3E0">
            <MapPin color="#F57C00" className="size-8" />
          </Flex>
        </Box>
        <Flex direction="column" justify="center" gap={1} pl={2} flex="1" minW={0}>
          <Text fontWeight="bold" fontSize={{ base: '14px', md: '16px' }}>
            DIFAL · Empresa #{codiEmpNum}
          </Text>
          <Text fontSize={{ base: '11px', md: '12px' }} color="gray.500">
            Período: {moment(dateStart).format('DD/MM/YYYY')} a{' '}
            {moment(dateEnd).format('DD/MM/YYYY')}
          </Text>
        </Flex>
      </Flex>

      <Flex
        direction={{ base: 'column', lg: 'row' }}
        justify="space-between"
        align={{ base: 'stretch', lg: 'center' }}
        mb={4}
        gap={3}
      >
        <MonthNavigator
          currentDate={currentDate}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
          onSelectMonth={handleSelectMonth}
        />
        <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={2} w={{ base: '100%', lg: 'auto' }}>
          <HStack spacing={1}>
            <Text fontSize="12px" color="gray.600" flexShrink={0}>
              Tipo:
            </Text>
            <Select
              size="sm"
              flex="1"
              value={tipo}
              background="body"
              onChange={e => setQueryParam('tipo', e.target.value)}
            >
              <option value="">Todos</option>
              <option value="entrada">Entrada</option>
              <option value="saida">Saída</option>
            </Select>
          </HStack>
          <HStack spacing={1}>
            <Text fontSize="12px" color="gray.600" flexShrink={0}>
              UF dest.:
            </Text>
            <Select
              size="sm"
              flex="1"
              value={ufDestino}
              background="body"
              onChange={e => setQueryParam('uf_destino', e.target.value)}
            >
              <option value="">Todas</option>
              {UF_LIST.map(u => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </Select>
          </HStack>
          <HStack spacing={1}>
            <Text fontSize="12px" color="gray.600" flexShrink={0}>
              Acum.:
            </Text>
            <Input
              size="sm"
              flex="1"
              placeholder="Código do acumulador"
              value={codiAcuInput}
              background="body"
              onChange={e => {
                setCodiAcuInput(e.target.value);
                debouncedCodiAcu(e.target.value);
              }}
            />
          </HStack>
        </SimpleGrid>
        {(isLoadingResumo || tableQuery.isFetching) && (
          <Spinner size="sm" color="primary" alignSelf="center" />
        )}
      </Flex>

      <DifalResumoEmpresaCards data={resumoData} isLoading={isLoadingResumo} />

      <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={4} mb={4}>
        <GridItem minW={0}>
          <DifalNotasTable
            data={items}
            pagination={pagination}
            isFetching={tableQuery.isFetching}
          />
        </GridItem>
        <GridItem minW={0}>
          <DifalPorUfChart data={porUfData} isLoading={isLoadingUf} />
        </GridItem>
      </Grid>
    </PageBox>
  );
}
